import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EducationPage } from '@/types/Education';
import * as educationApi from '@/api/educationApi';

interface EducationState {
    pages: EducationPage[];
    currentPage: EducationPage | null;
    loading: boolean;
    error: string | null;
}

const initialState: EducationState = {
    pages: [],
    currentPage: null,
    loading: false,
    error: null,
};

export const fetchEducationPages = createAsyncThunk(
    'education/fetchPages',
    async () => {
        const response = await educationApi.getEducationPages();
        return response;
    }
);

export const fetchEducationPageTree = createAsyncThunk(
    'education/fetchPageTree',
    async () => {
        const response = await educationApi.getEducationPageTree();
        return response;
    }
);

export const fetchEducationPage = createAsyncThunk(
    'education/fetchPage',
    async (id: number) => {
        const response = await educationApi.getEducationPage(id);
        return response;
    }
);

export const fetchEducationPageBySlug = createAsyncThunk(
    'education/fetchPageBySlug',
    async (slug: string) => {
        const response = await educationApi.getEducationPageBySlug(slug);
        return response;
    }
);

export const createEducationPage = createAsyncThunk(
    'education/createPage',
    async (pageData: any) => {
        const response = await educationApi.createEducationPage(pageData);
        return response;
    }
);

export const updateEducationPage = createAsyncThunk(
    'education/updatePage',
    async ({ id, pageData }: { id: number; pageData: any }) => {
        const response = await educationApi.updateEducationPage(id, pageData);
        return response;
    }
);

export const deleteEducationPage = createAsyncThunk(
    'education/deletePage',
    async (id: number) => {
        await educationApi.deleteEducationPage(id);
        return id;
    }
);

const educationSlice = createSlice({
    name: 'education',
    initialState,
    reducers: {
        setCurrentPage: (state, action: PayloadAction<EducationPage | null>) => {
            state.currentPage = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch pages
            .addCase(fetchEducationPages.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEducationPages.fulfilled, (state, action) => {
                state.loading = false;
                state.pages = action.payload;
            })
            .addCase(fetchEducationPages.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch pages';
            })
            // Fetch page tree
            .addCase(fetchEducationPageTree.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEducationPageTree.fulfilled, (state, action) => {
                state.loading = false;
                state.pages = action.payload.pages;
                state.currentPage = action.payload.currentPage || null;
            })
            .addCase(fetchEducationPageTree.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch page tree';
            })
            // Fetch single page
            .addCase(fetchEducationPage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEducationPage.fulfilled, (state, action) => {
                state.loading = false;
                state.currentPage = action.payload;
            })
            .addCase(fetchEducationPage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch page';
            })
            // Fetch page by slug
            .addCase(fetchEducationPageBySlug.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEducationPageBySlug.fulfilled, (state, action) => {
                state.loading = false;
                state.currentPage = action.payload;
            })
            .addCase(fetchEducationPageBySlug.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch page';
            })
            // Create page
            .addCase(createEducationPage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createEducationPage.fulfilled, (state, action) => {
                state.loading = false;
                state.pages.push(action.payload);
            })
            .addCase(createEducationPage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to create page';
            })
            // Update page
            .addCase(updateEducationPage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateEducationPage.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.pages.findIndex(page => page.id === action.payload.id);
                if (index !== -1) {
                    state.pages[index] = action.payload;
                }
                if (state.currentPage?.id === action.payload.id) {
                    state.currentPage = action.payload;
                }
            })
            .addCase(updateEducationPage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update page';
            })
            // Delete page
            .addCase(deleteEducationPage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteEducationPage.fulfilled, (state, action) => {
                state.loading = false;
                state.pages = state.pages.filter(page => page.id !== action.payload);
                if (state.currentPage?.id === action.payload) {
                    state.currentPage = null;
                }
            })
            .addCase(deleteEducationPage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to delete page';
            });
    },
});

export const { setCurrentPage, clearError } = educationSlice.actions;
export default educationSlice.reducer;

